import { App } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'translations/hooks';

import { db } from 'technical/db';
import { ringSyncService } from 'technical/db/sync/ring-sync.service';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';
import { colorPrimary } from 'ui/theme/colors';

import { BoardConfigurationData, RingData } from './types';

const BOARD_GAP = 48;
const SWITCH_INACTIVE_COLOR = 'rgba(0, 0, 0, 0.25)';
const SWITCH_WIDTH = 44;
const SWITCH_HEIGHT = 22;
const BUTTON_SIZE = 32;
const BUTTON_BORDER_RADIUS = 6;
const MARGIN_LEFT = 8;
const BUTTON_GAP = 8;

const SvgSwitch = ({
  value,
  onChange,
  x,
  y,
  disabled = false,
}: {
  value: boolean;
  onChange: () => void;
  x: number;
  y: number;
  disabled?: boolean;
}) => {
  const thumbX = value ? SWITCH_WIDTH - 11 : 11;
  return (
    <g
      transform={`translate(${x}, ${y})`}
      onClick={disabled ? undefined : onChange}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      opacity={disabled ? 0.5 : 1}
      pointerEvents="all"
    >
      <rect
        width={SWITCH_WIDTH}
        height={SWITCH_HEIGHT}
        rx={SWITCH_HEIGHT / 2}
        fill={value ? colorPrimary : SWITCH_INACTIVE_COLOR}
      />
      <circle cx={thumbX} cy={SWITCH_HEIGHT / 2} r={9} fill="white" />
    </g>
  );
};

const SvgArrowButton = ({
  onClick,
  x,
  y,
}: {
  onClick: () => void;
  x: number;
  y: number;
}) => (
  <g
    transform={`translate(${x}, ${y})`}
    onClick={onClick}
    style={{ cursor: 'pointer' }}
    pointerEvents="all"
  >
    <rect
      width={BUTTON_SIZE}
      height={BUTTON_SIZE}
      rx={BUTTON_BORDER_RADIUS}
      fill={colorPrimary}
    />
    <path
      d="M 10 16 L 22 16 M 18 12 L 22 16 L 18 20"
      stroke="white"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </g>
);

export const RingButtons = ({
  data,
  boardConfiguration,
  posY,
  setSelectedRing,
  canManageCompliance = true,
}: {
  data: RingData;
  boardConfiguration: BoardConfigurationData;
  posY: number;
  setSelectedRing?: (ringId: string) => void;
  canManageCompliance?: boolean;
}) => {
  const { t } = useTranslation();
  // To handle at higher level to show ring infos
  const [_selectedRing, _setSelectedRing] = useState<string | null>(null);
  const { message } = App.useApp();

  const handleCompliance = async () => {
    const ring = await ringSyncService.getOneById(data.id);
    if (!ring) {
      return;
    }

    const updateDate = dayjs().toISOString();
    if (ring.compliantDate) {
      await db.rings.update(data.id, {
        compliantDate: null,
        desynchronizedAt: updateDate,
      });
      return;
    }

    const hasOngoingDefect = await ringSyncService.hasOngoingDefect(data.id);
    if (hasOngoingDefect) {
      message.open({
        type: 'warning',
        content: t(
          'qualityControl.recordSegmentDefect.leftPanel.ringNotCompliantError',
        ),
      });
      return;
    }

    await db.rings.update(data.id, {
      compliantDate: updateDate,
      desynchronizedAt: dayjs().toISOString(),
    });
  };

  const baseX = boardConfiguration.ringBoardWidth + BOARD_GAP + MARGIN_LEFT;
  const centerY = posY + boardConfiguration.segment.height / 2;

  return (
    <g>
      <SvgSwitch
        value={!isUndefinedOrNull(data.compliantDate)}
        onChange={handleCompliance}
        x={baseX}
        y={centerY - SWITCH_HEIGHT / 2}
        disabled={!canManageCompliance}
      />
      <SvgArrowButton
        onClick={() => setSelectedRing?.(data.id)}
        x={baseX + SWITCH_WIDTH + BUTTON_GAP}
        y={centerY - BUTTON_SIZE / 2}
      />
    </g>
  );
};
